<template>
  <n-modal-background @close="$emit('close')" ref="bg">
    <n-modal-body>
      <main>
        <img class="side-image" :src="featureToUpgradeImage" />
        <div class="content">
          <div class="messages">
            <header>{{ $t(`featureRestriction.upgrades.${featureUpgrade}.title`) }}</header>
            <p class="subtitle">
              {{ $t(`featureRestriction.upgrades.${featureUpgrade}.subtitle`) }}
            </p>
            <p class="subtitle" v-if="!isAdmin">{{ $t('featureRestriction.contactAnAdminToUpgrade') }}</p>
          </div>
          <footer>
            <n-button @click="$refs.bg.close()" :label="$t('general.close')" variant="tertiary" />
            <n-button v-if="isAdmin" @click="openSalesLink()" :label="$t('general.talkToNotch')" variant="secondary" />
            <n-button v-if="showUpgradeCta" @click="upgrade()" :label="$t('general.upgrade')" />
          </footer>
        </div>
      </main>
    </n-modal-body>
  </n-modal-background>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { useUserStore } from '@/modules/user/stores/UserStore'
import { mapState } from 'pinia'
import User, { TeamRole } from '@/modules/user/models/User'
import { trackTalkToSalesClick } from '@/util/segment-tracking'
import { FeatureUpgrade } from '@/modules/featurerestriction/models/FeatureUpgrade'
import { BOOK_A_DEMO_URL } from '@/modules/featurerestriction/externalLinks'
import { canShowUpgradeCta, goToPlans } from '@/modules/team/util/planNavigation'

@Component({
  computed: mapState(useUserStore, ['currentUser'])
})
export default class UpgradeFeatureModal extends Vue {
  @Prop() featureUpgrade!: FeatureUpgrade
  private currentUser!: User

  private featureToImage = {
    [FeatureUpgrade.SPACES]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-create-unlimited-spaces.jpg`,
    [FeatureUpgrade.TEAM]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-expand-your-team.jpg`,
    [FeatureUpgrade.SLACK]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-connect-slack.jpg`,
    [FeatureUpgrade.SALESFORCE]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-connect-salesforce.jpg`,
    [FeatureUpgrade.HUBSPOT]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-connect-hubspot.jpg`,
    [FeatureUpgrade.SIGNATURE]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-sign-spaces.jpg`,
    // TODO: no dedicated artwork exists for the Developer Center yet. Reusing the Slack image
    // because API keys and webhooks are integration tooling; swap in a real asset once one is
    // uploaded to VITE_NOTCH_STATIC_STORAGE.
    [FeatureUpgrade.DEVELOPER]: `${import.meta.env.VITE_NOTCH_STATIC_STORAGE!}/upgrade-to-connect-slack.jpg`
  }

  get featureToUpgradeImage() {
    return this.featureToImage[this.featureUpgrade]
  }

  get isAdmin(): boolean {
    return this.currentUser.role === TeamRole.ADMIN
  }

  get showUpgradeCta(): boolean {
    return canShowUpgradeCta()
  }

  upgrade() {
    this.$emit('close')
    goToPlans(this.$router)
  }

  openSalesLink() {
    trackTalkToSalesClick('Upgrade to feature modal')
    window.open(BOOK_A_DEMO_URL, '_blank', 'noopener,noreferrer')
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

main {
  display: flex;
  flex-direction: column;
}

.side-image {
  object-fit: cover;
  height: $notch-space-90x;
}

.content {
  padding: $notch-space-6x;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $notch-space-8x;
  width: auto;
}

header {
  @include notch-text-heading-semibold-L;
  padding-bottom: $notch-space-2x;
}

.subtitle {
  @include notch-text-paragraph-regular-M;
  color: $notch-color-neutral-sand-600;
}

footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: $notch-space-2x;
}
</style>
